import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as _ from "lodash-es";
import { Socket } from "socket.io-client";
import "./VendorSidebar.scss";

// Models
import IVendorModel from "../../../../models/IVendorModel";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import IConnection_Token from "../../../../models/IConnection_Token";
import IPrice_Group from "../../../../models/IPrice_Group";
import ICatalog from "../../../../models/ICatalog";
import IAddress from "../../../../models/IAddress";
import INewAddress from "../../../../models/INewAddress";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import ICreateVendorDTO from "../../../../models/DTO/ICreateVendorDTO";
import IUpdateVendorDTO from "../../../../models/DTO/IUpdateVendorDTO";
import IOnUpdateVendorDTO from "../../../../models/DTO/IOnUpdateVendorDTO";

// Components
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { ReactHookInput } from "../../../shared/ReactHookInput/ReactHookInput";
import AddressBlock from "../../../shared/AddressBlock/AddressBlock";

type Props = {
    cancel: (refresh: boolean) => void;
    catalogs?: ICatalog[];
    supplierVendor?: IConnection_Token;
    supplierModel: ISupplierModel;
    userModel: IUserUI;
    userType: string;
    socket: Socket;
    priceGroups: IPrice_Group[];
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

type FormData = {
    name: string;
    email: string;
    active: boolean;
};

const createValidationSchema = () => {
    return yup.object().shape({
        name: yup.string().required("Name is required").min(1, "Name must be at least 1 character").trim(),
        email: yup.string().required("Email is required").email("Must be a valid email address").trim(),
        active: yup.boolean(),
    });
};

export const VendorSidebarFn = (props: Props) => {
    const { socket, supplierModel, userModel, supplierVendor, notifyFn } = props;

    // Initialize vendor model from props or create new
    const initialVendorModel = _.get(supplierVendor, "Vendor", { active: true } as IVendorModel);

    const [vendorModel, setVendorModel] = useState<IVendorModel>(initialVendorModel);
    const [billTo, setBillTo] = useState<IAddress>(_.get(supplierVendor, "Vendor.Bill_To", {} as IAddress));
    const [shipTo, setShipTo] = useState<IAddress>(_.get(supplierVendor, "Vendor.Ship_To", {} as IAddress));
    const [isSaving, setIsSaving] = useState(false);

    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
        setValue,
        watch,
    } = useForm<FormData>({
        resolver: yupResolver(createValidationSchema()) as any,
        defaultValues: {
            name: initialVendorModel.name || "",
            email: initialVendorModel.email || "",
            active: initialVendorModel.active !== undefined ? initialVendorModel.active : true,
        },
    });

    // Watch form values to keep vendorModel in sync
    const formValues = watch();

    useEffect(() => {
        // Set up socket listeners
        socket.on(EndpointsEnum.VendorsOnUpdateVendor, onUpdateVendor);

        return () => {
            // Clean up socket listeners
            socket.removeListener(EndpointsEnum.VendorsOnUpdateVendor, onUpdateVendor);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onUpdateVendor = (data: IOnUpdateVendorDTO) => {
        setIsSaving(false);

        if (data.error) {
            notifyFn(data.error);
            return;
        }

        if (data.vendor) {
            notifyFn(null, {
                title: "Vendor saved successfully",
                level: "success",
            });
            props.cancel(true);
        }
    };

    const onSubmit = (data: FormData) => {
        setIsSaving(true);

        // Build the payload for create or update
        const payload: ICreateVendorDTO | IUpdateVendorDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            options: {
                active: data.active,
            },
            billTo: billTo,
            shipTo: shipTo,
            name: data.name,
            email: data.email,
            shippingDetails: vendorModel.Shipping_Details || [],
            shippingAccounts: vendorModel.Shipping_Accounts || [],
        };

        if (supplierVendor && supplierVendor.Vendor) {
            // Update existing vendor
            (payload as IUpdateVendorDTO).tokenGuid = supplierVendor.guid;
            (payload as IUpdateVendorDTO).vendorGuid = supplierVendor.Vendor.guid;
            socket.emit(EndpointsEnum.VendorsUpdateVendor, payload);
        } else {
            // Create new vendor
            socket.emit(EndpointsEnum.VendorsCreateVendor, payload);
        }
    };

    const handleCancel = () => {
        props.cancel(false);
    };

    const handleActiveToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isActive = e.target.checked;
        setValue("active", isActive);
        setVendorModel({ ...vendorModel, active: isActive });
    };

    const handleBillToChange = (address: IAddress | INewAddress) => {
        setBillTo(address as IAddress);
    };

    const handleShipToChange = (address: IAddress | INewAddress) => {
        setShipTo(address as IAddress);
    };

    const handleCopyBillTo = () => {
        // Copy bill to address to ship to, preserving ship to's id and guid
        const copiedAddress = { ...billTo };
        if (shipTo.id) {
            copiedAddress.id = shipTo.id;
        }
        if (shipTo.guid) {
            copiedAddress.guid = shipTo.guid;
        }
        setShipTo(copiedAddress);
    };

    const isEditMode = !!(supplierVendor && supplierVendor.Vendor);
    const titleKey = isEditMode ? "edit_vendor" : "create_vendor";

    return (
        <div className="vendor-sidebar">
            <div className="sidebar-header">
                <OwTitle title={titleKey} />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="sidebar-content">
                <div className="form-section">
                    <div className="mb-3">
                        <OwLabel title="name" showIsReqAsterik={true} />
                        <ReactHookInput
                            register={register("name")}
                            error={errors.name}
                            name="name"
                            placeholder="name"
                            isRequired={true}
                            type="text"
                            disabled={isSaving}
                        />
                        {errors.name && <div className="text-danger text-sm mt-1">{errors.name.message}</div>}
                    </div>

                    <div className="mb-3">
                        <OwLabel title="email" showIsReqAsterik={true} />
                        <ReactHookInput
                            register={register("email")}
                            error={errors.email}
                            name="email"
                            placeholder="email"
                            isRequired={true}
                            type="email"
                            disabled={isSaving}
                        />
                        {errors.email && <div className="text-danger text-sm mt-1">{errors.email.message}</div>}
                    </div>

                    <div className="mb-3">
                        <OwToggle
                            fn={handleActiveToggle}
                            name="active"
                            label="active"
                            active={formValues.active}
                            labelOnRight={true}
                            disabled={isSaving}
                        />
                    </div>
                </div>

                <div className="form-section">
                    <div className="mb-3">
                        <OwLabel title="bill_to_address" />
                        <AddressBlock
                            address={billTo}
                            onChangeFn={handleBillToChange}
                            supplierModel={supplierModel}
                            userModel={userModel}
                            socket={socket}
                            disabled={isSaving}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                            <OwLabel title="ship_to_address" />
                            <div className="ms-2">
                                <OwButton title="copy_bill_to" fn={handleCopyBillTo} disabled={isSaving} className="btn-primary" />
                            </div>
                        </div>
                        <AddressBlock
                            address={shipTo}
                            onChangeFn={handleShipToChange}
                            supplierModel={supplierModel}
                            userModel={userModel}
                            socket={socket}
                            disabled={isSaving}
                        />
                    </div>
                </div>

                <div className="sidebar-footer">
                    <OwButton type="button" onClick={handleCancel} disabled={isSaving} className="btn-secondary me-2">
                        Cancel
                    </OwButton>
                    <OwButton type="submit" disabled={isSaving} className="btn-primary">
                        {isSaving ? "Saving..." : "Save"}
                    </OwButton>
                </div>
            </form>
        </div>
    );
};

export default VendorSidebarFn;
